/**
 * What the deck picker calls each deck.
 *
 * Full names do not fit: the top bar is 50px with a title, four tabs and a scope line already in
 * it, and `Japanese::Vocab` across four buttons is ~480px on its own. The leaf is what
 * distinguishes them — `Vocab`, `Names`, `Kanji`, `Facts` — so that is what shows.
 *
 * **Except when two leaves collide.** `Japanese::Vocab` beside `Spanish::Vocab` would render as two
 * buttons both saying `Vocab`, which is worse than a long label: it is a control that cannot be
 * used. Colliding names fall back to their full path, and only those; the others stay short.
 *
 * Pure and tested, on `libraryDrag.ts`'s terms — the interesting behaviour is the collision rule
 * and it is the kind of thing nobody notices is broken until they have two subjects.
 */
export function deckLabels(names: string[]): Map<string, string> {
  // `lastIndexOf` answers -1 for a top-level deck, and `slice(-1 + 2)` silently eats its first
  // character — `Facts` renders as `acts`. A top-level deck is its own leaf.
  const leafOf = (name: string) => {
    const at = name.lastIndexOf("::");
    return at === -1 ? name : name.slice(at + 2);
  };

  const seen = new Map<string, number>();
  for (const name of names) {
    const leaf = leafOf(name);
    seen.set(leaf, (seen.get(leaf) ?? 0) + 1);
  }

  const labels = new Map<string, string>();
  for (const name of names) {
    const leaf = leafOf(name);
    labels.set(name, (seen.get(leaf) ?? 0) > 1 ? name : leaf);
  }
  return labels;
}
